import {
  Component,
  JSXElement,
  createSignal,
  onMount,
  onCleanup,
  Show,
  untrack,
} from 'solid-js';
import CustomTooltip from '../CustomTooltip/CustomTooltip';
import DropdownOptions from '../DropdownOptions/DropdownOptions';
import DividedDropdownOptions from '../DividedDropdownOptions/DividedDropdownOptions';
import type { HybridDropdown } from '../DividedDropdownOptions/DividedDropdownOptions';
import Icon from '../Icon/Icon';
import { addComposedOutsideClickListener } from '../util';

export interface DropDownOptionItem {
  text: string;
  icon: string;
}

export interface DropdownSelectIconMenuProps {
  className?: string;
  classNameForOptions?: string;
  dropDownOptions: DropDownOptionItem[];
  tooltip?: string;
  iconPosition?: 'left' | 'right';
  selected: DropDownOptionItem;
  closeOnScroll?: boolean;
  selectionChanged?(selected: string): void;
  onMenuOpen?(isOpen: boolean): void;
  isDivided?: boolean;
  dividedKey?: string;
  dividedOptions?: HybridDropdown[];
  dividedSelectionChanged?(selected: string): void;
  toggleClicked(): void;
  displayText?: string;
  isActive?: boolean;
  showSelectedOnActive?: boolean;
  menuPosition:
    | 'left'
    | 'right'
    | 'top left'
    | 'top right'
    | 'middle'
    | 'top middle';
  addSeparators?: number[];
  'data-cy'?: string;
}

const DropdownSelectIconMenu: Component<DropdownSelectIconMenuProps> = (
  props
): JSXElement => {
  const [isOpen, setIsOpen] = createSignal(false);
  let dropDownRef: HTMLDivElement | undefined;

  const handleScroll = (): void => {
    if (props.closeOnScroll) {
      setIsOpen(false);
      props.onMenuOpen?.(false);
    }
  };

  const handleMenuToggle = (e: MouseEvent): void => {
    e.stopPropagation();
    e.preventDefault();
    const next = !isOpen();
    setIsOpen(next);
    props.onMenuOpen?.(next);
  };

  const handleToggleClick = (e: MouseEvent): void => {
    if (isOpen()) {
      e.stopPropagation();
      e.preventDefault();
      setIsOpen(false);
      props.onMenuOpen?.(false);
    }
    props.toggleClicked();
  };

  onMount(() => {
    const removeOutsideClickListener = addComposedOutsideClickListener(
      () => [dropDownRef],
      // eslint-disable-next-line solid/reactivity -- outside-click is an event handler registered on mount
      () => {
        untrack(() => {
          setIsOpen(false);
          props.onMenuOpen?.(false);
        });
      }
    );
    document.addEventListener('scroll', handleScroll, true);
    onCleanup(() => {
      removeOutsideClickListener();
      document.removeEventListener('scroll', handleScroll, true);
    });
  });

  const renderButtonContent = (): JSXElement => {
    if (!props.isDivided && !props.dividedKey) {
      return <Icon icon={props.selected.icon} size="icon-medium" />;
    }
    if (props.dividedKey === 'text' && !props.displayText) {
      return <span class="text-ellipsis">{props.selected.text}</span>;
    }
    if (props.dividedKey === 'both') {
      return (
        <>
          <Icon icon={props.selected.icon} size="icon-medium" />
          <span class="text-ellipsis">{props.selected.text}</span>
        </>
      );
    }
    if (props.dividedKey === 'text' && props.displayText) {
      return <span class="text-ellipsis">{props.displayText}</span>;
    }
    return <Icon icon={props.selected.icon} size="icon-medium" />;
  };

  const mainButton = (): JSXElement => (
    <button
      onClick={handleToggleClick}
      class={`button toolbar-primary icon-medium ${props.isActive ? 'active' : ''}`}
    >
      {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
      {renderButtonContent()}
    </button>
  );

  return (
    <div
      ref={dropDownRef}
      class={`dropdown-pane-container ${props.className ?? ''}`}
      data-testid={props['data-cy'] || 'dropdown-select-icon-menu'}
    >
      <div class="toggle-button-group split-button">
        <Show when={props.selected.text} fallback={mainButton()}>
          <CustomTooltip text={props.selected.text} alwaysShow>
            {/* eslint-disable-next-line custom/no-direct-solid-component-invocation -- Tracked by https://jira.trimble.tools/browse/TWENG-38 */}
            {mainButton()}
          </CustomTooltip>
        </Show>

        <Show
          when={props.tooltip}
          fallback={
            <button
              onClick={handleMenuToggle}
              class={`button toolbar-primary icon-medium dropdownpane-link ${isOpen() ? 'active' : ''}`}
            >
              <Icon
                icon={
                  isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'
                }
                size="icon-medium"
              />
            </button>
          }
        >
          <CustomTooltip text={props.tooltip} alwaysShow>
            <button
              onClick={handleMenuToggle}
              class={`button toolbar-primary icon-medium dropdownpane-link ${isOpen() ? 'active' : ''}`}
            >
              <Icon
                icon={
                  isOpen() ? 'tc-icon-arrow-drop-up' : 'tc-icon-arrow-drop-down'
                }
                size="icon-medium"
              />
            </button>
          </CustomTooltip>
        </Show>
      </div>

      <Show when={isOpen()}>
        <Show
          when={!props.isDivided}
          fallback={
            <DividedDropdownOptions
              isOpen={isOpen()}
              iconPosition={props.iconPosition}
              optionsType="both"
              multiarrayOptions={props.dividedOptions ?? []}
              selected={[props.selected.text]}
              selectionChanged={props.dividedSelectionChanged}
              class={props.classNameForOptions}
            />
          }
        >
          <DropdownOptions
            isOpen={isOpen()}
            iconPosition={props.iconPosition}
            optionsType="both"
            dropDownOptions={props.dropDownOptions}
            selected={
              props.showSelectedOnActive
                ? props.isActive
                  ? props.selected.text
                  : ''
                : props.selected.text
            }
            position={props.menuPosition}
            addSeparators={props.addSeparators}
            class={props.classNameForOptions}
            disabledItem={[]}
            onSelectionChanged={(selected) => {
              props.selectionChanged?.(selected);
              setIsOpen(false);
            }}
          />
        </Show>
      </Show>
    </div>
  );
};

export default DropdownSelectIconMenu;
